/**
 * Shared parsing for Apollo network/GraphQL errors — used by the client error
 * link and `useRedirectOnError` so they stay aligned on what counts as auth
 * (refresh) vs fatal redirect.
 *
 * Only 401 is treated as auth-by-status. 403 means ACL ("not your resource"),
 * so refreshing+retrying would loop forever (DEV-3138).
 */

type NetworkErrorLike = {
  message?: string;
  statusCode?: number;
  result?: {
    message?: string;
    error?: string;
    errors?: { message?: string }[];
  };
};

export const getNetworkErrorMessages = (networkError: unknown): string[] => {
  if (!networkError) {
    return [];
  }

  if (typeof networkError === 'string') {
    return [networkError];
  }

  const source = networkError as NetworkErrorLike;

  return [
    source.message,
    source.result?.message,
    source.result?.error,
    ...(source.result?.errors?.map((error) => error.message) || []),
  ].filter((message): message is string => Boolean(message));
};

export const getIsAuthHttpStatus = (networkError: unknown): boolean => {
  if (!networkError || typeof networkError !== 'object') {
    return false;
  }

  return (networkError as NetworkErrorLike).statusCode === 401;
};

/**
 * Apollo Server (and NestJS GraphQL) flag auth via `extensions.code` even when
 * the human message is generic — e.g. a resolver throwing ForbiddenException
 * surfaces as `{ message: '<anything>', extensions: { code: 'FORBIDDEN' } }`.
 * Recognize UNAUTHENTICATED and FORBIDDEN as auth so the refresh flow runs.
 */
type GraphQLErrorLike = { extensions?: { code?: string } | null };

export const hasAuthExtensionsCode = (
  graphQLErrors: ReadonlyArray<GraphQLErrorLike> | undefined
): boolean => {
  return Boolean(
    graphQLErrors?.some((graphQLError) => {
      const code = graphQLError.extensions?.code;

      return code === 'UNAUTHENTICATED' || code === 'FORBIDDEN';
    })
  );
};
